/*
 * This is the header.js entrypoint consisting of critical JS code, all bundled.
 * Whenever you need to introduce something which should run before the main Vue app hydrated, put it here.
 *
 * NB: the current boot sequence:
 * 0) booting absolutely critical sections (Sentry, splits, etc.)
 * 1) unconditionally exposing 1st party library code (YouTube events bus, etc.)
 * 2) monkey-patching `window._vwo_evq.push` to hook into redirect event
 * 3) setting up VWO init code and initiating VWO
 * 4) pre-pushing userType GA event, initiating GTM, starting VWO result polling
 *    + Demandbase personalization (deferred until OneTrust targeting consent)
 * 5) [in VWO polling complete, if no redirect] filling initial dataLayer events (BOGO, businessGeo)
 * 6) unconditionally initiating Matomo and pushing the pageview
*/

import { Logger } from '../../utils/logger';
import { initDemandbase } from './boot-demandbase';
import { getGeolocation } from './boot-geolocation';
import { initGTM } from './boot-gtm';
import { injectVWOCode } from './boot-inject-vwo-code.js';
import { initMatomo } from './boot-matomo';
import { increaseNavvaticCounter } from './boot-navvatic-counter';
import { sendBogoEvent } from './boot-send-bogo';
import { sendBusinessLocationEvent } from './boot-send-business-location';
import { initSplits } from './boot-splits';
import { injectSVGSprite } from './boot-svg-sprite';
import { monkeyPatchVWO, initVWO } from './boot-vwo';
import { initSentry } from './utils-sentry';
import { getVWOOutcome } from './utils-wait-for-vwo-outcome';
import { sendWebVitals } from './utils-web-vitals';
import { YoutubePlayerReadyBus } from './utils-youtube-player-ready-bus';

function safeBootSection(fn: () => void, logName: string) {
    try {
        fn();
    } catch (e) {
        Logger.error(e, `Failed to boot ${logName}`);
    }
}

async function safeBootSectionAsync(fn: () => Promise<void>, logName: string) {
    try {
        await fn();
    } catch (e) {
        Logger.error(e, `Failed to boot ${logName}`);
    }
}

export async function bootHeader() {
    await safeBootSectionAsync(injectSVGSprite, 'bootSVGSprite');
    safeBootSection(initSplits, 'splits');
    safeBootSection(sendWebVitals, 'sendWebVitals');
    safeBootSection(initSentry, 'initSentry');
    safeBootSection(increaseNavvaticCounter, 'increaseNavvaticCounter');

    window.youtubePlayerReadyBus = new YoutubePlayerReadyBus();

    safeBootSection(monkeyPatchVWO, 'monkeyPatchVWO');
    safeBootSection(injectVWOCode, 'injectVWOCode');
    safeBootSection(initVWO, 'initVWO');
    safeBootSection(initGTM, 'initGTM');
    safeBootSection(initDemandbase, 'initDemandbase');
    safeBootSectionAsync(initMatomo, 'initMatomo');

    if (typeof window !== 'undefined') {
        const consentGiven = window.OptanonActiveGroups?.includes('C0002');
        if (consentGiven) {
            window.ytTracker?.init();
        }
    }

    // Code which requires VWO decision making goes here (only some GA events for now)
    const vwoOutcome = await getVWOOutcome();
    if (vwoOutcome !== 'redirect') {
        await getGeolocation();
        sendBusinessLocationEvent();
        sendBogoEvent();
    }
}
